import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { Observable, of } from 'rxjs';
import { catchError, concatMap, map, mergeMap, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import * as ItemTypeActions from './actions';
import { environment } from 'src/environments/environment';
import { IItem } from '@app/models/item.model';
import { RootStoreState } from '../../../../root-store';
import { getPageNum, getFilterSortData, getProjectId } from './selectors';
import { NgxSpinnerService } from 'ngx-spinner';
import { HttpService } from '@surveyname/common';

@Injectable()
export class ItemsEffects {
  constructor(
    private http: HttpService,
    private action$: Actions,
    private store$: Store<RootStoreState.State>,
    private spinner: NgxSpinnerService
  ) { }

  GetItems$: Observable<Action> = createEffect(() =>
    this.action$.pipe(
      ofType(ItemTypeActions.GetItemsAction),
      mergeMap(action => {
        return this.http
          .post(
            `${environment.apiUrl}/item/project/${action.payload.projectId}/pageNumber/${action.payload.pageNum}/itemsPerPage/100`,
            action.payload.filterSortData
          )
          .pipe(
            map((items: any[]) => {
              return ItemTypeActions.ItemsLoadedAction({ payload: items });
            }),
            catchError((error: Error) => {
              return of(ItemTypeActions.ErrorItemsAction(error));
            })
          );
      }
      )
    )
  );


  RefreshItems$: Observable<Action> = createEffect(() =>
    this.action$.pipe(
      ofType(ItemTypeActions.RefreshItemsAction),
      withLatestFrom(
        this.store$.select(getPageNum),
        this.store$.select(getFilterSortData),
        this.store$.select(getProjectId)
      ),
      mergeMap(([action, pageNum, sortFilterData, projectId]) => {
        return this.http
          .post(
            `${environment.apiUrl}/item/project/${projectId}/pageNumber/${pageNum ? pageNum : 1}/itemsPerPage/100`,
            sortFilterData
          )
          .pipe(
            map((items: any[]) => {
              return ItemTypeActions.ItemsLoadedAction({ payload: items });
            }),
            catchError((error: Error) => {
              return of(ItemTypeActions.ErrorItemsAction(error));
            })
          );
      }
      )
    )
  );

  RefreshPagination$: Observable<Action> = createEffect(() =>
  this.action$.pipe(
    ofType(ItemTypeActions.RefreshItemsAction),
    mergeMap((action) => {
      return this.http
        .get(
          `${environment.apiUrl}/item/total/project/${action.payload.projectId}`
        )
        .pipe(
          map((count: any[]) => {
            return ItemTypeActions.TotalItemsLoadedAction({ payload: count });
          }),
          catchError((error: Error) => {
            return of(ItemTypeActions.ErrorItemsAction(error));
          })
        );
    }
    )
  )
);

  GetTotalItems$: Observable<Action> = createEffect(() =>
    this.action$.pipe(
      ofType(ItemTypeActions.GetTotalItemsAction),
      mergeMap(action =>
        this.http
          .get(
            `${environment.apiUrl}/item/total/project/${action.payload.projectId}`
          )
          .pipe(
            map((count: number) => {
              return ItemTypeActions.TotalItemsLoadedAction({ payload: count });
            }),
            catchError((error: Error) => {
              return of(ItemTypeActions.ErrorItemsAction(error));
            })
          )
      )
    )
  );

  PersistItem$: Observable<Action> = createEffect(() =>
    this.action$.pipe(
      ofType(ItemTypeActions.PersistItemAction),
      mergeMap(action =>
        this.http.post(`${environment.apiUrl}/item`, action.payload).pipe(
          map((item: IItem) => {
            return ItemTypeActions.RefreshItemsAction({
              payload: {
                projectId: action.payload.projectId,
                resetCheckedItems: false,
                allowAddNewItem: true
              }
            });
          }),
          catchError((error: Error) => {
            return of(ItemTypeActions.ErrorItemsAction(error));
          })
        )
      )
    )
  );

  PersistUpdateItem$: Observable<Action> = createEffect(() =>
    this.action$.pipe(
      ofType(ItemTypeActions.PersistUpdateItemAction),
      mergeMap(action =>
        this.http.put(`${environment.apiUrl}/item`, action.payload.item).pipe(
          map((item: IItem) =>
            ItemTypeActions.UpdateItemAction({
              payload: { item: action.payload.item }
            }),
          ),
          catchError((error: Error) => {
            return of(ItemTypeActions.ErrorItemsAction(error));
          })
        )
      )
    )
  );

  UpdateItems$: Observable<Action> = createEffect(() =>
    this.action$.pipe(
      ofType(ItemTypeActions.BeginUpdateItemsAction),
      mergeMap(action =>
        this.http.put(`${environment.apiUrl}/item/update-many`, action.payload).pipe(
          map((items) =>
            ItemTypeActions.UpdateItemsFinishedAction({
              payload: action.payload
            }),
          ),
          catchError((error: Error) => {
            return of(ItemTypeActions.ErrorItemsAction(error));
          })
        )
      )
    )
  );

  RecalculateRulesForField$: Observable<Action> = createEffect(() =>
  this.action$.pipe(
    ofType(ItemTypeActions.BeginRecalculateRulesForFieldsAction),
    mergeMap(action =>
      this.http.post(`${environment.apiUrl}/rule/recalculate`, action.payload).pipe(
        map((message) =>
          ItemTypeActions.RecalculateRulesForFieldsFinishedAction({
            payload: {message}
          }),
        ),
        catchError((error: Error) => {
          return of(ItemTypeActions.ErrorItemsAction(error));
        })
      )
    )
  )
);

  CalculateRules$: Observable<Action> = createEffect(() =>
    this.action$.pipe(
      ofType(ItemTypeActions.UpdateItemsFinishedAction),
      map(action => action.payload),
      map(updates => updates.map(update => ({itemId: update.item._id, changedFields: update.changedFields}))),
      mergeMap(updates =>
        of(ItemTypeActions.BeginCalculateRulesForItemAction({ payload: updates })
        )
      )
    )
  );

  CalculateRulesMassEdit$: Observable<Action> = createEffect(() =>
  this.action$.pipe(
    ofType(ItemTypeActions.UpdateMassItemsAction),
    map(action => action.payload),
    map(updates => updates.itemIds.map(itemId =>
      ({itemId, changedFields: updates.updateFields.map(fieldWithValue => fieldWithValue.techName)}))),
    mergeMap(updates =>
      of(ItemTypeActions.BeginCalculateRulesForItemAction({ payload: updates })
      )
    )
  )
);


  PersistUpdateMassItems$: Observable<Action> = createEffect(() =>
    this.action$.pipe(
      ofType(ItemTypeActions.PersistMassUpdateItemsAction),
      mergeMap(action =>
        this.http.put(`${environment.apiUrl}/item/mass`, action.payload).pipe(
          map(item => {
            return ItemTypeActions.UpdateMassItemsAction({
              payload: {
                itemIds: action.payload.itemIds,
                updateFields: action.payload.updateFields
              }
            });
          }),
          catchError((error: Error) => {
            return of(ItemTypeActions.ErrorItemsAction(error));
          })
        )
      )
    )
  );

  CopyAndPasteItems$: Observable<Action> = createEffect(() =>
    this.action$.pipe(
      ofType(ItemTypeActions.BeginCopyAndPasteItemsAction),
      mergeMap(action =>
        this.http
          .post(`${environment.apiUrl}/item/duplicate`, action.payload)
          .pipe(
            map(items => {
              return ItemTypeActions.RefreshItemsAction({
                payload: {
                  projectId: action.payload.projectId,
                  resetCheckedItems: false
                }
              });
            }),
            catchError((error: Error) => {
              return of(ItemTypeActions.ErrorItemsAction(error));
            })
          )
      )
    )
  );

  CutAndPasteItems$: Observable<Action> = createEffect(() =>
    this.action$.pipe(
      ofType(ItemTypeActions.BeginCutAndPasteItemsAction),
      mergeMap(action =>
        this.http.put(`${environment.apiUrl}/item/paste`, action.payload).pipe(
          map(items => {
            return ItemTypeActions.RefreshItemsAction({
              payload: {
                projectId: action.payload.projectId,
                resetCheckedItems: true
              }
            });
          }),
          catchError((error: Error) => {
            return of(ItemTypeActions.ErrorItemsAction(error));
          })
        )
      )
    )
  );

  DuplicateItems$: Observable<Action> = createEffect(() =>
    this.action$.pipe(
      ofType(ItemTypeActions.BeginDuplicateItemsAction),
      mergeMap(action =>
        this.http.post(`${environment.apiUrl}/item/duplicate`, action.payload).pipe(
          map(items => {
            return ItemTypeActions.RefreshItemsAction({
              payload: {
                projectId: action.payload.projectId,
                resetCheckedItems: false
              }
            });
          }),
          catchError((error: Error) => {
            return of(ItemTypeActions.ErrorItemsAction(error));
          })
        )
      )
    )
  );

  RemoveItems$: Observable<Action> = createEffect(() =>
    this.action$.pipe(
      ofType(ItemTypeActions.BeginRemoveItemsAction),
      mergeMap(action =>
        this.http.post(`${environment.apiUrl}/item/mass-delete`, action.payload).pipe(
          map(result => {
            return ItemTypeActions.RefreshItemsAction({
              payload: {
                projectId: action.payload.projectId,
                resetCheckedItems: true
              }
            });
          }),
          catchError((error: Error) => {
            return of(ItemTypeActions.ErrorItemsAction(error));
          })
        )
      )
    )
  );

  ChangeOrder$: Observable<Action> = createEffect(() =>
    this.action$.pipe(
      ofType(ItemTypeActions.BeginChangeOrderAction),
      mergeMap(action => {
        this.spinner.show();
        return this.http.put(`${environment.apiUrl}/item/change-order`, action.payload).pipe(
          map(items => {
            this.spinner.hide();
            return ItemTypeActions.RefreshItemsAction({
              payload: {
                projectId: action.payload.projectId,
                resetCheckedItems: true
              }
            });
          }),
          catchError((error: Error) => {
            return of(ItemTypeActions.ErrorItemsAction(error));
          })
        );
      }
      )
    )
  );

  CalculateRulsForItem$: Observable<Action> = createEffect(() =>
    this.action$.pipe(
      ofType(ItemTypeActions.BeginCalculateRulesForItemAction),
      mergeMap(action => {
        return this.http
          .post(
            `${environment.apiUrl}/rule/calculate`,
            action.payload
          )
          .pipe(
            map((changes: any) => {
              return ItemTypeActions.CalculateRulesForItemFinishedAction({ payload: { changes } });
            }),
            catchError((error: Error) => {
              return of(ItemTypeActions.ErrorItemsAction(error));
            })
          );
      }
      )
    )
  );

  AddManyItems$: Observable<Action> = createEffect(() =>
  this.action$.pipe(
    ofType(ItemTypeActions.BeginAddManyItemsAction),
    mergeMap(action =>
      this.http
        .post(`${environment.apiUrl}/item/add-many`, action.payload)
        .pipe(
          map(items => {
            return ItemTypeActions.RefreshItemsAction({
              payload: {
                projectId: action.payload.projectId,
                resetCheckedItems: false
              }
            });
          }),
          catchError((error: Error) => {
            return of(ItemTypeActions.ErrorItemsAction(error));
          })
        )
    )
  )
);
}
